import PageLoader from '@/components/layouts/PageLoader';
import {
  Button,
  Input,
  Modal,
  PhoneInput,
  ToggleSwitch,
} from '@/components/modules';
import { useMeQuery } from '@/remote/user-management/fetch-me';
import { useUpdateProfile } from '@/remote/user-management/update-profile';
import type { User } from '@/types/site';
import * as React from 'react';
import { Suspense, useState } from 'react';

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ProfileEditModal({
  isOpen,
  onClose,
}: Readonly<ProfileEditModalProps>) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="내 정보 수정"
      size="4xl"
      maxHeight="max-h-[90vh]"
    >
      <Suspense
        fallback={
          <div className="flex items-center justify-center h-[80vh]">
            <PageLoader />
          </div>
        }
      >
        <DataFetchLayer onClose={onClose}>
          {({
            user,
            formData,
            handleInputChange,
            handleSave,
            isLoading,
            // isAddressEdit,
            // setIsAddressEdit,
          }) => (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="이름"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
                <Input
                  label="이메일"
                  type="email"
                  placeholder="이메일 주소를 입력하세요"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
                {/* <Input
                  label="기존 주소"
                  value={user.fullAddress || '주소가 설정되지 않음'}
                  disabled
                  onChange={() => {}}
                />
                <div className="flex items-end justify-start">
                  <Button
                    onClick={() => setIsAddressEdit(true)}
                    variant="primary"
                    size="medium"
                    className="w-fit"
                  >
                    주소 변경하기
                  </Button>
                </div> */}
                {/* <div
                  className={`md:col-span-2 transition-all duration-300 ease-in-out overflow-hidden ${
                    isAddressEdit
                      ? 'opacity-100 transform translate-y-0'
                      : 'max-h-0 opacity-0 transform-translate-y-2'
                  }`}
                >
                  <AddressInput
                    className="mb-2"
                    label="새 주소"
                    onChange={(value) =>
                      handleInputChange('fullAddress', value)
                    }
                  />
                </div> */}
                <PhoneInput
                  label="휴대폰번호"
                  type="mobile"
                  value={formData.mobilePhone}
                  onChange={(value) => handleInputChange('mobilePhone', value)}
                />
                <PhoneInput
                  label="전화번호"
                  type="landline"
                  value={formData.phone}
                  onChange={(value) => handleInputChange('phone', value)}
                />
                <ToggleSwitch
                  label="SMS 수신여부"
                  value={formData.smsReceive}
                  onChange={(value) => handleInputChange('smsReceive', value)}
                />
                <Input
                  label="아이디"
                  value={user.loginId}
                  onChange={() => {}}
                  readOnly
                />
              </div>

              <div className="flex space-x-3 pt-6">
                <Button
                  onClick={handleSave}
                  variant="primary"
                  size="medium"
                  fullWidth
                  disabled={isLoading}
                >
                  {isLoading ? '저장 중...' : '변경사항 저장'}
                </Button>
                <Button
                  onClick={onClose}
                  variant="secondary"
                  size="medium"
                  fullWidth
                  disabled={isLoading}
                >
                  취소
                </Button>
              </div>
            </div>
          )}
        </DataFetchLayer>
      </Suspense>
    </Modal>
  );
}

const DataFetchLayer = ({
  children,
  onClose,
}: {
  children: ({
    user,
    formData,
    handleInputChange,
    handleSave,
    isLoading,
    isAddressEdit,
    setIsAddressEdit,
  }: {
    user: User;
    formData: {
      name: string;
      email: string;
      fullAddress: string;
      mobilePhone: string;
      phone: string;
      smsReceive: boolean;
    };
    handleInputChange: (field: string, value: string | boolean) => void;
    handleSave: () => Promise<void>;
    isLoading: boolean;
    isAddressEdit: boolean;
    setIsAddressEdit: (value: boolean) => void;
  }) => React.ReactNode;
  onClose: () => void;
}) => {
  const { data: user } = useMeQuery();
  const updateProfileMutation = useUpdateProfile({ userId: user?.id || '' });
  const [isAddressEdit, setIsAddressEdit] = useState(false);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    fullAddress: user?.fullAddress || '',
    mobilePhone: user?.mobilePhone || '',
    phone: user?.phone || '',
    smsReceive: user?.smsReceive || false,
  });

  // user 데이터가 로드되면 formData 업데이트
  React.useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        fullAddress: user.fullAddress || '',
        mobilePhone: user.mobilePhone || '',
        phone: user.phone || '',
        smsReceive: user.smsReceive || false,
      });
    }
  }, [user]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      await updateProfileMutation.mutateAsync({
        name: formData.name,
        email: formData.email,
        fullAddress: formData.fullAddress,
        mobilePhone: formData.mobilePhone || '',
        phone: formData.phone || '',
        smsReceive: formData.smsReceive,
      });

      alert('프로필이 성공적으로 수정되었습니다.');
      onClose();
    } catch (error) {
      console.error('프로필 수정 실패:', error);
      alert('프로필 수정 중 오류가 발생했습니다.');
    }
  };

  if (!user) {
    return <PageLoader />;
  }

  return children({
    user,
    formData,
    handleInputChange,
    handleSave,
    isLoading: updateProfileMutation.isPending,
    isAddressEdit,
    setIsAddressEdit,
  });
};
